<script setup lang="ts">import { ref as _ref } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'
import { isPhone } from '@consumer/utils/breakpoints'
import { preventNonNumericInput } from '@consumer/utils/number'

withDefaults(defineProps<{ gift: GiftTemplate }>(), {  })

const emit = defineEmits<{ stepComplete: [] }>()

const DEFAULT_AMOUNT = 100
const MINIMUM_AMOUNT = 5
const MAXIMUM_AMOUNT = 1000
const SUGGESTED_AMOUNTS = [
  10,
  25,
  50,
  75,
  100,
  150,
  200,
  300,
  400,
  500,
  750,
  1000,
]

let customAmount = _ref<number>()

function checkIfComplete () {
  if (__props.gift.amount) emit('stepComplete')
}

function selectAmount (value: number) {
  setAmount(value)
  customAmount.value = undefined
  checkIfComplete()
}

function setAmount (value: number) {
  if (value < MINIMUM_AMOUNT) return

  __props.gift.amount = value
}

function setCustomAmount (event: InputEvent) {
  const target = event.target as HTMLInputElement
  const amount = parseInt(target.value) || DEFAULT_AMOUNT
  if (amount > MAXIMUM_AMOUNT) {
    target.value = String(MAXIMUM_AMOUNT)
    customAmount.value = MAXIMUM_AMOUNT
    setAmount(MAXIMUM_AMOUNT)
  }
  else {
    setAmount(amount)
  }
}

watchEffect(() => {
  if (!__props.gift.amount) {
    setAmount(DEFAULT_AMOUNT)
  }

  if (!SUGGESTED_AMOUNTS.includes(__props.gift.amount!)) {
    customAmount.value = __props.gift.amount
  }
})
</script>

<template>
  <div>
    <div
      class="w-full grid grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-3
      text-v2-secondary text-sm lg:text-base"
    >
      <GButton
        v-for="amount, i in SUGGESTED_AMOUNTS"
        :key="i"
        :label="`$${amount}`"
        class="g-button-tab"
        :class="{ active: gift.amount === amount }"
        :style="{ height: isPhone ? '48px' : '52px' }"
        name="nc_amount"
        :alt="amount"
        secondary
        @click="selectAmount(amount)"
      />
    </div>

    <GDivider class="flex items-center justify-center" style="margin: 1.5rem 0;">
      <div class="bg-surface-grey px-4 font-semibold z-0 text-v2-secondary">
        or enter a custom amount
      </div>
    </GDivider>

    <div class="flex items-center gap-2">
      <GTextInput
        v-model="customAmount"
        type="number"
        min="10"
        max="1000"
        step="5"
        name="nc_amount_custom"
        placeholder="Any amount up to $1,000"
        placeholderAlignment="center"
        class="text-center custom-amount-input"
        wrapperClass="flex-grow"
        :class="customAmount ? '!border-primary-base !bg-primary-pale': ''"
        inputmode="numeric"
        @keypress="preventNonNumericInput"
        @keypress.enter="checkIfComplete"
        @input="setCustomAmount"
      />
    </div>
  </div>
</template>
